import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageSEO } from '../data/seoPages';

const BASE_URL = 'https://petdays.homy.hk';
const SOCIAL_IMAGE = `${BASE_URL}/social-preview.svg`;

const SEOHead = ({
  language,
  structuredData,
  activeTab = 'pet',
  anniversaryType = null
}) => {
  const { pathname } = useLocation();
  const pageSEO = useMemo(
    () => getPageSEO(pathname, language, activeTab),
    [pathname, language, activeTab]
  );
  const canonicalUrl = `${BASE_URL}${pageSEO.canonicalPath}`;
  const keywords = generateAIKeywords(language, activeTab, anniversaryType);

  useEffect(() => {
    document.title = pageSEO.title;
    updateMetaTag('name', 'description', pageSEO.description);
    updateMetaTag('name', 'keywords', keywords);
    updateMetaTag('name', 'author', 'Homy Tech Limited');
    updateMetaTag('name', 'robots', 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1');
    updateMetaTag('name', 'googlebot', 'index,follow');
    updateLinkTag('canonical', canonicalUrl);

    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((tag) => tag.remove());
    addAlternate('zh-TW', `${canonicalUrl}${canonicalUrl.includes('?') ? '&' : '?'}lang=zh`);
    addAlternate('en', `${canonicalUrl}${canonicalUrl.includes('?') ? '&' : '?'}lang=en`);
    addAlternate('x-default', canonicalUrl);

    updateMetaTag('property', 'og:type', 'website');
    updateMetaTag('property', 'og:url', canonicalUrl);
    updateMetaTag('property', 'og:title', pageSEO.title);
    updateMetaTag('property', 'og:description', pageSEO.description);
    updateMetaTag('property', 'og:image', SOCIAL_IMAGE);
    updateMetaTag('property', 'og:image:width', '1200');
    updateMetaTag('property', 'og:image:height', '630');
    updateMetaTag('property', 'og:image:alt', 'Pet Days 寵物紀念日計算器');
    updateMetaTag('property', 'og:locale', language === 'zh' ? 'zh_TW' : 'en_US');
    updateMetaTag('property', 'og:site_name', 'Pet Days 寵物紀念日');

    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', pageSEO.title);
    updateMetaTag('name', 'twitter:description', pageSEO.description);
    updateMetaTag('name', 'twitter:image', SOCIAL_IMAGE);
    updateMetaTag('name', 'twitter:image:alt', 'Pet Days 寵物紀念日計算器');

    if (structuredData) {
      let script = document.querySelector('script[data-type="structured-data"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.type = 'structured-data';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    document.documentElement.lang = language === 'zh' ? 'zh-TW' : 'en';
  }, [pageSEO, language, canonicalUrl, keywords, structuredData]);

  return null;
};

const updateMetaTag = (attribute, value, content) => {
  let tag = document.querySelector(`meta[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const updateLinkTag = (rel, href) => {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
};

const addAlternate = (language, href) => {
  const link = document.createElement('link');
  link.rel = 'alternate';
  link.hreflang = language;
  link.href = href;
  document.head.appendChild(link);
};

const generateAIKeywords = (language, activeTab, type) => {
  if (language === 'zh') {
    const base = '寵物紀念日計算,貓咪生日計算,狗狗領養日追蹤,寵物里程碑紀錄,相處天數計算器,寵物健康日記工具,毛孩成長紀錄';
    return activeTab === 'standard'
      ? `日期差計算,工作日計算工具,兩個日期間隔天數,倒數日計算器,${base}`
      : `${base},${type || ''} 紀念日分享`;
  }

  const base = 'pet anniversary counter,cat birthday tracker,dog adoption day calculator,pet milestone badges,days with pet counter,pet growth journal';
  return activeTab === 'standard'
    ? `date difference calculator,business days counter,days between dates tool,countdown clock,${base}`
    : `${base},${type || ''} anniversary sharing`;
};

export default SEOHead;